export type Selection = { start: number; end: number };

export type CommandResult = { value: string; selection: Selection };

type Command = (value: string, selection: Selection) => CommandResult;

const wrapSelection = (value: string, selection: Selection, marker: string): CommandResult => {
  const { start, end } = selection;
  const selected = value.slice(start, end);
  const next = `${value.slice(0, start)}${marker}${selected}${marker}${value.slice(end)}`;
  return { value: next, selection: { start: start + marker.length, end: end + marker.length } };
};

export const bold: Command = (value, selection) => wrapSelection(value, selection, "**");

export const italic: Command = (value, selection) => wrapSelection(value, selection, "_");

export const inlineCode: Command = (value, selection) => wrapSelection(value, selection, "`");

const lineRange = (value: string, selection: Selection) => {
  const lineStart = value.lastIndexOf("\n", selection.start - 1) + 1;
  const lineEndIndex = value.indexOf("\n", selection.end);
  const lineEnd = lineEndIndex === -1 ? value.length : lineEndIndex;
  return { lineStart, lineEnd };
};

const transformLines = (value: string, selection: Selection, transform: (line: string, index: number) => string): CommandResult => {
  const { lineStart, lineEnd } = lineRange(value, selection);
  const block = value.slice(lineStart, lineEnd);
  const lines = block.split("\n");
  const nextLines = lines.map(transform);
  const nextBlock = nextLines.join("\n");
  const next = `${value.slice(0, lineStart)}${nextBlock}${value.slice(lineEnd)}`;
  const delta = nextBlock.length - block.length;
  return { value: next, selection: { start: lineStart, end: lineEnd + delta } };
};

const HEADING_PATTERN = /^(#{1,3})\s/;

export const heading: Command = (value, selection) =>
  transformLines(value, selection, (line) => {
    const match = line.match(HEADING_PATTERN);
    if (!match) {
      return `# ${line}`;
    }
    const level = match[1]!.length;
    if (level >= 3) {
      return line.slice(match[0].length);
    }
    return `${"#".repeat(level + 1)} ${line.slice(match[0].length)}`;
  });

const toggleLinePrefix = (line: string, prefix: string, pattern: RegExp) => (pattern.test(line) ? line.replace(pattern, "") : `${prefix}${line}`);

export const bulletList: Command = (value, selection) => transformLines(value, selection, (line) => toggleLinePrefix(line, "- ", /^-\s/));

export const numberedList: Command = (value, selection) =>
  transformLines(value, selection, (line, index) => toggleLinePrefix(line, `${index + 1}. `, /^\d+\.\s/));

export const checkboxItem: Command = (value, selection) => transformLines(value, selection, (line) => toggleLinePrefix(line, "- [ ] ", /^-\s\[[ x]\]\s/));

export const blockQuote: Command = (value, selection) => transformLines(value, selection, (line) => toggleLinePrefix(line, "> ", /^>\s/));

export const link: Command = (value, selection) => {
  const { start, end } = selection;
  const selected = value.slice(start, end);
  if (!selected) {
    const placeholder = "[label](url)";
    const next = `${value.slice(0, start)}${placeholder}${value.slice(end)}`;
    return { value: next, selection: { start: start + 1, end: start + 6 } };
  }
  const next = `${value.slice(0, start)}[${selected}](url)${value.slice(end)}`;
  const urlStart = start + selected.length + 3;
  return { value: next, selection: { start: urlStart, end: urlStart + 3 } };
};

export const fencedCodeBlock: Command = (value, selection) => {
  const { start, end } = selection;
  const selected = value.slice(start, end);
  const next = `${value.slice(0, start)}\`\`\`\n${selected}\n\`\`\`${value.slice(end)}`;
  const innerStart = start + 4;
  return { value: next, selection: { start: innerStart, end: innerStart + selected.length } };
};
